<i18n>
{
	"en": {
		"needAccept": "Need acceptance from driver",
		"saveAsTemplate": "Save as Template",
		"template": "Template",
		"failedToSave": "Failed to save trip",
		"fromAddress": "From Address",
		"toAddress": "To Address",
		"etd": "Planned Departure",
		"eta": "Planned Arrival"
	},
	"no": {
		"needAccept": "Trenger bekreftelse fra sjåfør",
		"saveAsTemplate": "Lagre som mal",
		"template": "Mal",
		"failedToSave": "Kunne ikke lagre tur",
		"fromAddress": "Fra adresse",
		"toAddress": "Til adresse",
		"etd": "Planlagt avgang",
		"eta": "Planlagt ankomst"
	}
}
</i18n>

<template>
	<div class="new-trip">
		<div class="grid-top">

			<div v-if="$route.path.includes('operational')">
				<t-form-group
					v-if="trip.id"
					:title="$t('template')"
					rowGap="5px"
				>
					<t-button
						@click="onSaveAsTemplate"
					>{{ $t('saveAsTemplate') }}</t-button>
				</t-form-group>

				<TripTemplateFormInput
					v-else
					:selectedItem="selectedTemplate"
					@select="onLoadTemplate($event)"
				/>
			</div>

			<OperationalStatusFormInput
				:selectedItem="trip.status"
				:availableStatuses="selectableStatuses"
				showPopoverOnFocus
				@change="setStatus($event) && focusNext()"
			/>
			<TripTypesFormInput
				:selectedItem="trip.type"
				@select="setType"
			/>
			<ClerkInput
				:value="trip.clerk"
				focusNextOnSelected
				@change="setClerk"
			/>
		</div>

		<hr >
		<div class="grid-left">
			<TripAddressInput
				ref="fromAddress"
				:title="$t('fromAddress')"
				:value="trip.fromAddress"
				:isInvalid="Boolean(errors.fromAddress)"
				:validationKey="errors.fromAddress"
				focusNextOnSelected
				@change="setFromAddress"
			/>
			<t-date-time-input
				:title="$t('etd')"
				:isInvalid="Boolean(errors.etd)"
				:validationKey="errors.etd"
				:dateTimeObject="trip.etd"
				@date-input="setEtd({ dateValue: $event, hasTime: null })"
				@time-input="setEtd({ timeValue: $event, hasTime: Boolean($event) })"
			/>
			<ActualTime
				field="atd"
				:dateTimeObject="trip.atd"
			/>

			<TripAddressInput
				:title="$t('toAddress')"
				:value="trip.toAddress"
				:isInvalid="Boolean(errors.toAddress)"
				:validationKey="errors.toAddress"
				focusNextOnSelected
				@change="setToAddress"
			/>
			<t-date-time-input
				:title="$t('eta')"
				:isInvalid="Boolean(errors.eta)"
				:validationKey="errors.eta"
				:dateTimeObject="trip.eta"
				@date-input="setEta({ dateValue: $event, hasTime: null })"
				@time-input="setEta({ timeValue: $event, hasTime: Boolean($event) })"
			/>
			<ActualTime
				field="ata"
				:dateTimeObject="trip.ata"
			/>

			<ViaLocations :viaLocations="trip.viaLocations" />
		</div>

		<div class="grid-right">
			<CargoCarriersInput
				ref="cargoCarriersInput"
				:value="trip.cargoCarrier"
				@change="setCargoCarrier($event)
					&& Object.keys($event).length
					&& focusNext({ target: getFirstInputElementByRef($refs.cargoCarriersInput) })"
			/>
			<VendorInput
				ref="VendorsFormInput"
				:value="trip.vendor"
				:isInvalid="Boolean(errors.vendor)"
				:validationKey="errors.vendor"
				@change="setVendor($event)
					&& Object.keys($event).length
					&& focusNext({ target: getFirstInputElementByRef($refs.VendorsFormInput) })"
			/>
		</div>

		<Legs v-if="tripLoaded" class="grid-bottom"/>
		<t-overlay
			:isShown="!tripLoaded"
			loaderMarginBottom="50px"
			absolute
			loader
		/>
	</div>
</template>

<script>
import ClerkInput from '@/modules/clerks/ClerkInput.vue';
import CargoCarriersInput from '@/modules/cargoCarriers/CargoCarriersInput.vue';
import VendorInput from '@/modules/vendors/VendorInput.vue';
import OperationalStatusFormInput from '@/modules/operationalStatus/OperationalStatusFormInput.vue';
import TripTypesFormInput from '@/modules/tripTypes/TripTypesFormInput.vue';
import TripAddressInput from '@/modules/tripAddresses/TripAddressInput';
import TripTemplateFormInput from '@/modules/tripTemplates/TripTemplateFormInput.vue';
import Legs from './Legs/Legs.vue';
import ActualTime from './Time/ActualTime';
import ViaLocations from './ViaLocations.vue';

import {
	eventBus,
	statuses,
} from '@/core';
import { mapState, mapActions, mapMutations } from './store/newTrip.store.js';

export default {
	name: 'newTripMain',
	components: {
		ClerkInput,
		CargoCarriersInput,
		VendorInput,
		OperationalStatusFormInput,
		TripTypesFormInput,
		TripAddressInput,
		TripTemplateFormInput,
		Legs,
		ActualTime,
		ViaLocations,
	},
	props: {
		trip: Object,
	},
	data() {
		return {
			selectableStatuses: Object.values(statuses.trip),
			selectedTemplate: undefined,
		};
	},
	computed: {
		...mapState(['tripLoaded', 'errors']),
	},

	methods: {
		...mapActions([
			'setStatus',
			'setType',
			'setClerk',
			'setFromAddress',
			'setToAddress',
			'setEtd',
			'setEta',
			'setCargoCarrier',
			'setVendor',
		]),
		...mapMutations(['loadTemplate',]),
		onLoadTemplate(template) {
			this.selectedTemplate = template;
			this.loadTemplate(template);
		},
		onSaveAsTemplate() {
			eventBus.$emit('setTripTemplateDetails', this.trip);
			eventBus.$emit('toggleTripTemplateDetailsModal', true);
		},
		getFirstInputElementByRef(ref) {
			return ref.$el.querySelector('input');
		},
	},
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
@import '~@/components/evergreen/scss/text';

.new-trip {
	display: grid;
	row-gap: 20px;
	grid-template-columns: 708px 1fr;
	grid-template-areas:
		"top	top"
		".....	....."
		"left	right"
		"bottom bottom";
	margin-top: 20px;
	transition: 0.3s;
}

.grid-top {
	display: grid;
	row-gap: 20px;
	grid-template-columns: 	repeat(4, 1fr);
	grid-area: top;

	& > * {
			padding-right: 20px;
			width: auto;
	}
	& > *:last-child {
			padding-right: 0px;
	}
}
.grid-left {
	display: grid;
	row-gap: 20px;
	column-gap: 20px;
	grid-area: left;
	grid-template-columns: 	minmax(min-content, 1fr)
							repeat(2, minmax(min-content, max-content));
	grid-template-rows: auto auto 1fr;
}

.grid-right {
	display: grid;
	row-gap: 20px;
	grid-area: right;
	grid-template-columns: 1fr;
	grid-template-rows: repeat(2, minmax(min-content, max-content));

	border-left: 1px solid get-color($border, 'default');
	padding-left: 19px;
	margin-left: 20px;
}

.grid-bottom {
	grid-area: bottom
}

hr {
	grid-column: 1 / -1;
	height: 1px;
	background: get-color($border, 'default');
	width: 100%;
	margin-top: 0;
	margin-bottom: 0;
	border: 0;
	grid-column: span 2;
}
</style>
